import { useEffect, useRef, useState } from "react";
import { Eraser, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";

export function WaiverModal({ open, onOpenChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", dob: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#00F0FF";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
  }, [open]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSigned(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    if (!agreed) return toast.error("Please agree to the waiver terms.");
    if (!hasSigned) return toast.error("Please sign in the box.");
    setSubmitting(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      await api.post("/submissions/waiver", { ...form, agreed, signature });
      localStorage.setItem("tgr_waiver_signed", new Date().toISOString());
      toast.success("Waiver signed! You're cleared for check-in.");
      onOpenChange(false);
      setForm({ name: "", email: "", dob: "" });
      setAgreed(false);
      clear();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-inkalt text-slate-200" data-testid="waiver-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight text-white">
            Digital <span className="text-neon-cyan glow-cyan">Waiver</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Sign once before you arrive and skip the check-in line. Required for gaming stations, VR treadmill, and events.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-36 overflow-y-auto rounded-md border border-white/10 bg-ink p-3 text-xs leading-relaxed text-slate-400" data-testid="waiver-terms">
          <p className="mb-2 font-semibold text-slate-300">THE GAME ROOM — PARTICIPATION & LIABILITY WAIVER</p>
          <p className="mb-2">I understand that use of gaming equipment, VR omnidirectional treadmill systems, arcade cabinets, and participation in events at The Game Room involves physical activity and inherent risks. I voluntarily assume all risks of personal injury or property damage.</p>
          <p className="mb-2">I agree to follow all staff instructions and posted safety rules, including VR treadmill height/health requirements and session time limits. Guests under 16 must be supervised by a parent or guardian, and minors require a parent/guardian signature.</p>
          <p>I release The Game Room, its owners, and staff from liability for injuries or losses arising from ordinary negligence, to the fullest extent permitted by Connecticut law. I consent to photos/video taken on premises being used for promotional purposes unless I opt out at the front desk.</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="waiver-name" className="text-xs text-slate-400">Full name *</Label>
            <Input id="waiver-name" data-testid="waiver-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border-white/10 bg-ink text-white" placeholder="Player One" />
          </div>
          <div>
            <Label htmlFor="waiver-email" className="text-xs text-slate-400">Email *</Label>
            <Input id="waiver-email" data-testid="waiver-email-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border-white/10 bg-ink text-white" placeholder="you@example.com" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="waiver-dob" className="text-xs text-slate-400">Date of birth (minors: parent signs)</Label>
            <Input id="waiver-dob" data-testid="waiver-dob-input" type="date" value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
              className="border-white/10 bg-ink text-white" />
          </div>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <Label className="text-xs text-slate-400">Signature (draw with mouse/finger) *</Label>
            <button type="button" data-testid="waiver-clear-signature-button" onClick={clear}
              className="inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-neon-magenta">
              <Eraser className="h-3 w-3" /> Clear
            </button>
          </div>
          <canvas ref={canvasRef} data-testid="waiver-signature-canvas"
            className="h-40 w-full touch-none rounded-md border border-dashed border-neon-cyan/40 bg-ink"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-400">
          <Checkbox data-testid="waiver-agree-checkbox" checked={agreed}
            onCheckedChange={(v) => setAgreed(Boolean(v))} className="mt-0.5 border-white/30" />
          I have read and agree to the participation & liability waiver above.
        </label>

        <button data-testid="waiver-submit-button" onClick={submit} disabled={submitting}
          className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-md bg-neon-cyan px-4 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60">
          <ShieldCheck className="h-4 w-4" />
          {submitting ? "Signing..." : "Sign Waiver"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
